import { makeStyles, Typography } from '@material-ui/core';
import {
  boschBlue,
  boschGray,
} from '../../modules/theme/boschColors';

const useStyles = makeStyles(theme => ({
  /*
   * Wraps the iframe in a card-like container so it doesn't sit flush
   * against the page edges. The thin border + rounded corners match
   * the `InfoCard` chrome used elsewhere (catalog cards, home page
   * cards) so embedded content reads as "another card on this page".
   */
  card: {
    border: `1px solid ${boschGray[85]}`,
    borderRadius: 6,
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
  },
  header: {
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${boschGray[90]}`,
    backgroundColor: boschGray[95],
    fontSize: '0.7rem',
    fontWeight: 700,
    color: boschGray[40],
    textTransform: 'uppercase',
    letterSpacing: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  link: {
    fontSize: '0.7rem',
    color: boschBlue[50],
    textDecoration: 'none',
    fontWeight: 600,
    '&:hover': { textDecoration: 'underline' },
  },
  frame: {
    display: 'block',
    width: '100%',
    border: 'none',
  },
  error: {
    padding: theme.spacing(2),
    color: boschGray[40],
    fontStyle: 'italic',
    fontSize: '0.85rem',
  },
}));

export type IframeEmbedProps = {
  url: string;
  title?: string;
  height?: number;
};

/*
 * Tight allow-list of hosts an embedded iframe can load.
 *
 * Why a hard allow-list:
 *
 *   - An open iframe widget would let any admin paste any URL,
 *     including pages that intentionally break out of their frame
 *     (X-Frame-Options: DENY), serve unsafe scripts, or steal hover
 *     focus from the parent page.
 *   - More importantly, it would also accept hostile URLs from a
 *     compromised admin account, turning a "content page" into a
 *     phishing surface that lives inside the company portal under a
 *     trusted domain.
 *
 * The default list below covers the systems the Digital Factory team
 * actually wants to embed (Confluence wiki, Grafana boards, Metabase
 * dashboards, status pages, etc.). Add new ENTRIES, not new schemes:
 *
 *   - Allowed only via HTTPS (the regex requires `https://`).
 *   - Allowed only matching the listed hostnames (suffix match on the
 *     host portion of the URL). Subdomains pass; sibling domains do
 *     not.
 *
 * If an admin needs an embed from a host that's not on this list, the
 * right path is to:
 *
 *   1. Add the hostname here in a PR (visible & reviewable change).
 *   2. Re-run the Scaffolder template / edit the YAML.
 *
 * Keep this list narrow on purpose.
 */
const ALLOWED_HOSTS: readonly string[] = [
  'inside-docupedia.bosch.com',
  'bosch-digitalfactory.com.br',
  'bosch-digital-factory.com',
  'kuma.bosch-digital-factory.com',
  'sonarqube.bosch-digital-factory.com',
  'dev.azure.com',
  'youtube.com',
  'www.youtube.com',
  'youtube-nocookie.com',
];

/** Returns the parsed URL only when the input is a valid HTTPS URL
 *  whose host matches one of the allow-list entries. Anything else
 *  yields `undefined`, which the component turns into an inline
 *  error message — never a silent fallback to an arbitrary URL. */
const safeParseAllowedUrl = (raw: string): URL | undefined => {
  try {
    const parsed = new URL(raw);
    if (parsed.protocol !== 'https:') return undefined;
    const host = parsed.hostname.toLowerCase();
    const allowed = ALLOWED_HOSTS.some(
      h => host === h || host.endsWith(`.${h}`),
    );
    return allowed ? parsed : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Inline iframe embed for trusted internal systems (Confluence,
 * Grafana, Metabase, status pages, etc.).
 *
 * Why an allow-list instead of a "permissive" widget:
 *   - Lets corporate sec/compliance sign off on which third-party
 *     hosts can be embedded; new hosts require a PR (audit trail).
 *   - Protects against an admin-account compromise serving arbitrary
 *     content under the portal's trusted origin.
 *
 * If you need an embed from a new host, add the hostname to
 * `ALLOWED_HOSTS` above and ship the change like any other code
 * review.
 */
export const IframeEmbed = ({
  url,
  title,
  height = 480,
}: IframeEmbedProps) => {
  const classes = useStyles();
  const safe = safeParseAllowedUrl(url);

  if (!safe) {
    return (
      <div className={classes.card}>
        <div className={classes.error}>
          The URL <code>{url}</code> is not in the embed allow-list. Add
          its hostname to <code>ALLOWED_HOSTS</code> in{' '}
          <code>IframeEmbed.tsx</code> and ship the change in a PR.
        </div>
      </div>
    );
  }

  return (
    <div className={classes.card}>
      <div className={classes.header}>
        <Typography component="span" style={{ font: 'inherit' }}>
          {title ?? safe.hostname}
        </Typography>
        <a
          href={safe.toString()}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.link}
        >
          Open in new tab ↗
        </a>
      </div>
      <iframe
        src={safe.toString()}
        title={title ?? safe.hostname}
        className={classes.frame}
        style={{ height }}
        loading="lazy"
        /*
         * Why this exact sandbox set:
         *   - `allow-scripts`: most dashboards (Grafana / Metabase / Confluence)
         *     break without JS. We accept the risk because the host is
         *     allow-listed above.
         *   - `allow-same-origin`: needed for Confluence / Grafana /
         *     Metabase to load their own session cookies for rendering.
         *     Combined with the host allow-list it's still scoped to
         *     trusted internal systems.
         *   - `allow-popups`: lets "open in new tab" links inside the
         *     iframe work (e.g. Confluence article links).
         *   - We DON'T grant `allow-top-navigation` or `allow-forms`
         *     — neither is needed for any of the allow-listed hosts
         *     and both materially increase the blast radius if a host
         *     is somehow compromised.
         */
        sandbox="allow-scripts allow-same-origin allow-popups"
        referrerPolicy="no-referrer-when-downgrade"
      />
    </div>
  );
};
